import { useState } from 'react';
import { Check, Plus } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { StepUpDialog } from '@/components/auth/StepUpDialog';
import { ApiError } from '@/lib/api';
import {
  useLinkedAccounts,
  useSwitchAccount,
  type LinkedAccount,
} from '@/lib/api/auth';
import { cn } from '@/lib/utils';

/** Инициалы для аватара строки: «Багрянцев Евгений» → «БЕ», иначе первая буква почты. */
function initials(account: LinkedAccount): string {
  const name = (account.full_name ?? '').trim();
  if (name) {
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase();
  }
  return (account.email[0] ?? '?').toUpperCase();
}

function accountLabel(account: LinkedAccount): string {
  return account.full_name?.trim() || account.email;
}

/**
 * Переключатель аккаунтов в меню под аватаром (AK-469, вариант B founder'а
 * 2026-07-28, wireframe — PR #569, экран 1).
 *
 * Почему хук, а не один компонент: `StepUpDialog` нельзя рендерить внутри
 * `<DropdownMenuContent>` — Radix размонтирует содержимое меню при закрытии,
 * а меню закрывается ровно в момент выбора аккаунта, и диалог исчезал бы, не
 * успев открыться. Поэтому пункты меню и диалог возвращаются раздельно:
 * `menuItems` кладём внутрь меню, `stepUpDialog` — рядом с `<DropdownMenu>`.
 *
 * Отсюда же правило про ошибку: путь «с кодом» закрывает меню, поэтому отказ
 * переключения на нём показывает диалог (он остаётся открытым до успеха), а
 * не строка внутри `menuItems` — её на экране уже нет. Путь без кода меню не
 * закрывает (`event.preventDefault`), там ошибка живёт в меню.
 *
 * @param enabled — меню открыто. Список аккаунтов запрашиваем только тогда,
 *   а не на каждой загрузке страницы.
 */
export function useAccountSwitcher(options: { enabled: boolean }) {
  const accountsQuery = useLinkedAccounts({ enabled: options.enabled });
  const switchMut = useSwitchAccount();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stepUpFor, setStepUpFor] = useState<LinkedAccount | null>(null);

  const accounts = accountsQuery.data?.items ?? [];

  const doSwitch = async (userId: string, stepUpToken?: string) => {
    setError(null);
    setPendingId(userId);
    try {
      await switchMut.mutateAsync({
        user_id: userId,
        step_up_token: stepUpToken,
      });
      // Жёсткий переход, а не router.navigate: перезагрузка гарантированно
      // сбрасывает состояние сторов (карта, сезон, scope), где могли остаться
      // данные предыдущего аккаунта. Кэш TanStack Query чистит сама мутация.
      window.location.assign('/');
    } catch (err) {
      setPendingId(null);
      setError(
        err instanceof ApiError
          ? err.detail || 'Не удалось переключить аккаунт'
          : 'Не удалось переключить аккаунт',
      );
    }
  };

  const handleSelect = (account: LinkedAccount, event: Event) => {
    if (account.is_current) return;

    if (account.is_expired) {
      // Сессия протухла — пароль всё равно спросят, ведём на вход с уже
      // подставленной почтой, чтобы не набирать её заново.
      window.location.assign(`/login?email=${encodeURIComponent(account.email)}`);
      return;
    }

    if (account.requires_step_up) {
      setError(null); // чужая ошибка от прошлой попытки в новом диалоге — баг
      setStepUpFor(account);
      return; // меню закрывается штатно, диалог живёт снаружи
    }

    // Держим меню открытым, пока идёт переключение: иначе клик выглядит как
    // «ничего не произошло» до самой перезагрузки страницы.
    event.preventDefault();
    void doSwitch(account.user_id);
  };

  const menuItems = (
    <>
      {accountsQuery.isLoading && (
        <div className="space-y-1 px-2 py-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      )}

      {accounts.map((account) => {
        const isPending = pendingId === account.user_id;
        return (
          <DropdownMenuItem
            key={account.user_id}
            onSelect={(e) => handleSelect(account, e)}
            /* Пока одно переключение в полёте — остальные строки не кликаются. */
            disabled={pendingId !== null}
            className={cn(
              'items-start gap-2 py-2',
              account.is_current && 'bg-accent/60',
              account.is_expired && 'text-muted-foreground',
            )}
          >
            <Avatar
              className={cn('h-7 w-7 shrink-0', account.is_expired && 'opacity-60')}
            >
              <AvatarFallback className="text-[11px]">
                {initials(account)}
              </AvatarFallback>
            </Avatar>
            {/* Метка («Спросит код» / «Нужно войти заново») — отдельной
                строкой под почтой, а не справа от неё: в строке она съедала
                половину ширины меню и имя с почтой уходили в многоточие
                («Багрянцев Евгени…», «m.petrova@kris…»). Founder 2026-07-29:
                имя и почта должны быть видны целиком. */}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium leading-tight">
                {accountLabel(account)}
              </p>
              <p className="truncate text-xs text-muted-foreground leading-tight">
                {account.email}
              </p>
              {!isPending && !account.is_current && account.is_expired && (
                <Badge
                  variant="secondary"
                  className="mt-1 px-2 py-0 text-[10px] font-medium"
                >
                  Нужно войти заново
                </Badge>
              )}
              {!isPending &&
                !account.is_current &&
                !account.is_expired &&
                account.requires_step_up && (
                  <Badge
                    variant="secondary"
                    className="mt-1 px-2 py-0 text-[10px] font-medium"
                  >
                    Спросит код
                  </Badge>
                )}
            </div>
            {isPending && (
              <span className="shrink-0 text-xs text-muted-foreground">
                Переключаем…
              </span>
            )}
            {!isPending && account.is_current && (
              <Check className="h-4 w-4 shrink-0 text-primary" />
            )}
          </DropdownMenuItem>
        );
      })}

      {error && (
        <p className="px-2 py-1 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}

      {/* «Добавить аккаунт» показываем ВСЕГДА (кроме loading-состояния), а не
          только когда есть привязанные — иначе курица-яйцо: первый аккаунт
          привязать неоткуда, потому что CTA появляется лишь когда что-то уже
          привязано. Founder на UAT staging 2026-08-06 увидел меню только с
          «Профиль/Выйти» без пути добавить второй аккаунт. Сепаратор ставим
          всегда — он визуально отделяет CTA от «Профиль/Выйти» ниже. */}
      {!accountsQuery.isLoading && (
        <>
          <DropdownMenuItem
            onSelect={() => window.location.assign('/login')}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Добавить аккаунт
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      )}
    </>
  );

  const stepUpDialog = stepUpFor ? (
    <StepUpDialog
      open
      onOpenChange={(open) => {
        if (!open) {
          setStepUpFor(null);
          setError(null);
        }
      }}
      purpose="switch_account"
      targetUserId={stepUpFor.user_id}
      /* Почта, а не имя: у founder'а два аккаунта с одинаковым ФИО. */
      accountLabel={stepUpFor.email}
      /* Диалог остаётся открытым до успеха: меню к этому моменту уже
         размонтировано вместе с `menuItems`, и «Переключаем…» / отказ
         человеку больше показать негде. */
      busy={pendingId === stepUpFor.user_id}
      busyLabel="Переключаем…"
      externalError={pendingId === null ? error : null}
      onConfirmed={(token) => {
        void doSwitch(stepUpFor.user_id, token);
      }}
    />
  ) : null;

  return { menuItems, stepUpDialog };
}
